import { useState, useMemo } from 'react';
import { Service } from '@turizi/shared';
import { SERVICE_TEMPLATES } from '@/types/templates';
import { getAttrValue } from '@/lib/models-utils';

interface UseServiceFiltersProps {
    services: Service[];
    selectedCategory?: string | null;
    initialFilters?: Record<string, any[]>;
}

export function useServiceFilters({
    services,
    selectedCategory,
    initialFilters,
}: UseServiceFiltersProps) {

    // 1. Identify Attribute Keys
    const allAttributeKeys = useMemo(() => {
        const attributeMap = new Map<string, { key: string; sort: number }>();
        const category = selectedCategory || services[0]?.category;
        const template = category ? SERVICE_TEMPLATES[category as keyof typeof SERVICE_TEMPLATES] : null;

        if (template?.self_attrs) {
            template.self_attrs.forEach(attr => {
                if (attr.key && !attributeMap.has(attr.key)) {
                    // Exclude standard keys we handle differently or don't want to filter by
                    if (['tourism_type', 'amenities', 'website', 'policies'].includes(attr.key)) return;

                    attributeMap.set(attr.key, {
                        key: attr.key,
                        sort: attr.sort ?? 999999
                    });
                }
            });
        }
        return Array.from(attributeMap.values())
            .sort((a, b) => a.sort - b.sort)
            .map(item => item.key);
    }, [services, selectedCategory]);

    const { providerAttributeKeys } = useMemo(() => {
        const providerAttrMap = new Map<string, { key: string; sort: number }>();
        services.forEach(service => {
            service.providers?.forEach(provider => {
                provider.self_attrs?.forEach(attr => {
                    if (attr.key && !providerAttrMap.has(attr.key)) {
                        if (['tourism_type', 'amenities', 'website', 'policies'].includes(attr.key)) return;
                        providerAttrMap.set(attr.key, {
                            key: attr.key,
                            sort: attr.sort ?? 999999
                        });
                    }
                });
            });
        });
        const keys = Array.from(providerAttrMap.values())
            .sort((a, b) => a.sort - b.sort)
            .map(item => item.key);
        return { providerAttributeKeys: keys };
    }, [services]);

    // 2. Initialize Filters State
    // We use a function to initialize state so it recalculates if keys change significantly, 
    // but usually we want to persist filters unless reset.
    // Here we merge provided initialFilters (e.g. from URL) with the structure.
    const defaultFilters = useMemo(() => {
        const filters: Record<string, any[]> = {};
        allAttributeKeys.forEach(key => filters[key] = []);
        providerAttributeKeys.forEach(key => filters[`provider_${key}`] = []);
        filters.businessName = [];
        filters.sellerName = [];
        filters.publishPrice = [];
        filters.hasCost = [];
        filters.hasLimit = [];

        if (initialFilters) {
            Object.keys(initialFilters).forEach(key => {
                if (filters.hasOwnProperty(key)) {
                    filters[key] = initialFilters[key];
                }
            });
        }
        return filters;
    }, [allAttributeKeys, providerAttributeKeys, initialFilters]);

    const [filters, setFilters] = useState(defaultFilters);

    // 3. Calculate All Available Options (Base Set)
    const allFilterOptions = useMemo(() => {
        const options: Record<string, Set<any>> = {};

        // Initialize Sets
        options.businessName = new Set<string>();
        options.sellerName = new Set<string>();
        options.publishPrice = new Set<boolean>();
        options.hasCost = new Set<boolean>();
        options.hasLimit = new Set<boolean>();

        // Initialize dynamic keys
        allAttributeKeys.forEach(key => options[key] = new Set());
        providerAttributeKeys.forEach(key => options[`provider_${key}`] = new Set());

        services.forEach(service => {
            // Providers
            service.providers?.forEach(provider => {
                if (provider.name) options.businessName.add(provider.name);
                provider.self_attrs?.forEach(attr => {
                    if (attr.key && attr.value !== null && attr.value !== undefined && attr.value !== '') {
                        const optionKey = `provider_${attr.key}`;
                        if (options[optionKey]) options[optionKey].add(attr.value);
                    }
                });
            });

            // Seller
            if (service.seller?.name) options.sellerName.add(service.seller.name);

            // Publish Price
            if (service.publish_price !== null && service.publish_price !== undefined) options.publishPrice.add(service.publish_price);

            // Has Cost
            const hasCost = (service.sell_costs?.length || 0) > 0;
            options.hasCost.add(hasCost);

            // Has Limit
            const hasLimit = (service.default_limit || 0) > 0;
            options.hasLimit.add(hasLimit);

            // Attributes
            allAttributeKeys.forEach(key => {
                const value = getAttrValue(service, key);
                if (value !== null && value !== undefined && value !== '') options[key].add(value);
            });
        });

        const sortedOptions: Record<string, any[]> = {};
        Object.entries(options).forEach(([key, valueSet]) => {
            const values = Array.from(valueSet);
            if (values.every(v => !isNaN(Number(v)))) {
                sortedOptions[key] = values.sort((a, b) => Number(a) - Number(b));
            } else {
                sortedOptions[key] = values.sort();
            }
        });
        return sortedOptions;
    }, [services, allAttributeKeys, providerAttributeKeys]);

    // 4. Calculate Available Options based on current filters (Refined Set)
    const availableFilterOptions = useMemo(() => {
        const hasActiveFilters = Object.values(filters).some(filterValues => filterValues && filterValues.length > 0);
        if (!hasActiveFilters) return allFilterOptions;

        const options: Record<string, Set<any>> = {};
        // Initialize Sets
        options.businessName = new Set<string>();
        options.sellerName = new Set<string>();
        options.publishPrice = new Set<boolean>();
        options.hasCost = new Set<boolean>();
        options.hasLimit = new Set<boolean>();

        allAttributeKeys.forEach(key => options[key] = new Set());
        providerAttributeKeys.forEach(key => options[`provider_${key}`] = new Set());

        const allFilterKeys = [
            'businessName', 'sellerName', 'publishPrice', 'hasCost', 'hasLimit',
            ...allAttributeKeys,
            ...providerAttributeKeys.map(key => `provider_${key}`)
        ];

        allFilterKeys.forEach(currentFilterKey => {
            const otherFilters = { ...filters };
            delete otherFilters[currentFilterKey];

            const filteredServices = services.filter(service => {
                for (const [key, filterValues] of Object.entries(otherFilters)) {
                    if (filterValues && filterValues.length > 0) {
                        if (key === 'businessName') {
                            const hasMatchingBusiness = service.providers?.some(provider => filterValues.includes(provider.name));
                            if (!hasMatchingBusiness) return false;
                        } else if (key === 'sellerName') {
                            if (!filterValues.includes(service.seller?.name)) return false;
                        } else if (key === 'publishPrice') {
                            if (!filterValues.includes(service.publish_price)) return false;
                        } else if (key === 'hasCost') {
                            const hasCost = (service.sell_costs?.length || 0) > 0;
                            if (!filterValues.includes(hasCost)) return false;
                        } else if (key === 'hasLimit') {
                            const hasLimit = (service.default_limit || 0) > 0;
                            if (!filterValues.includes(hasLimit)) return false;
                        } else if (key.startsWith('provider_')) {
                            const attrKey = key.replace('provider_', '');
                            const hasMatchingProvider = service.providers?.some(provider => {
                                const providerAttr = provider.self_attrs?.find(attr => attr.key === attrKey);
                                return providerAttr && filterValues.includes(providerAttr.value);
                            });
                            if (!hasMatchingProvider) return false;
                        } else {
                            const attrValue = getAttrValue(service, key);
                            if (!filterValues.includes(attrValue)) return false;
                        }
                    }
                }
                return true;
            });

            // Collect available options for current key from filtered services
            filteredServices.forEach(service => {
                if (currentFilterKey === 'businessName') {
                    service.providers?.forEach(provider => {
                        if (provider.name) options.businessName.add(provider.name);
                    });
                } else if (currentFilterKey === 'sellerName') {
                    if (service.seller?.name) options.sellerName.add(service.seller.name);
                } else if (currentFilterKey === 'publishPrice') {
                    if (service.publish_price !== null && service.publish_price !== undefined) options.publishPrice.add(service.publish_price);
                } else if (currentFilterKey === 'hasCost') {
                    const hasCost = (service.sell_costs?.length || 0) > 0;
                    options.hasCost.add(hasCost);
                } else if (currentFilterKey === 'hasLimit') {
                    const hasLimit = (service.default_limit || 0) > 0;
                    options.hasLimit.add(hasLimit);
                } else if (currentFilterKey.startsWith('provider_')) {
                    const attrKey = currentFilterKey.replace('provider_', '');
                    service.providers?.forEach(provider => {
                        provider.self_attrs?.forEach(attr => {
                            if (attr.key === attrKey && attr.value !== null && attr.value !== undefined && attr.value !== '') {
                                options[currentFilterKey].add(attr.value);
                            }
                        });
                    });
                } else {
                    const value = getAttrValue(service, currentFilterKey);
                    if (value !== null && value !== undefined && value !== '') options[currentFilterKey].add(value);
                }
            });
        });

        const sortedOptions: Record<string, any[]> = {};
        Object.entries(options).forEach(([key, valueSet]) => {
            const values = Array.from(valueSet);
            if (values.every(v => !isNaN(Number(v)))) {
                sortedOptions[key] = values.sort((a, b) => Number(a) - Number(b));
            } else {
                sortedOptions[key] = values.sort();
            }
        });
        return sortedOptions;
    }, [services, filters, allAttributeKeys, providerAttributeKeys, allFilterOptions]);


    // 5. Final Filtered Services
    const filteredServices = useMemo(() => {
        return services.filter(service => {
            for (const [key, filterValues] of Object.entries(filters)) {
                if (filterValues && filterValues.length > 0) {
                    if (key === 'businessName') {
                        const hasMatchingBusiness = service.providers?.some(provider => filterValues.includes(provider.name));
                        if (!hasMatchingBusiness) return false;
                    } else if (key === 'sellerName') {
                        if (!filterValues.includes(service.seller?.name)) return false;
                    } else if (key === 'publishPrice') {
                        if (!filterValues.includes(service.publish_price)) return false;
                    } else if (key === 'hasCost') {
                        const hasCost = (service.sell_costs?.length || 0) > 0;
                        if (!filterValues.includes(hasCost)) return false;
                    } else if (key === 'hasLimit') {
                        const hasLimit = (service.default_limit || 0) > 0;
                        if (!filterValues.includes(hasLimit)) return false;
                    } else if (key.startsWith('provider_')) {
                        const attrKey = key.replace('provider_', '');
                        const hasMatchingProvider = service.providers?.some(provider => {
                            const providerAttr = provider.self_attrs?.find(attr => attr.key === attrKey);
                            return providerAttr && filterValues.includes(providerAttr.value);
                        });
                        if (!hasMatchingProvider) return false;
                    } else {
                        const attrValue = getAttrValue(service, key);
                        if (!filterValues.includes(attrValue)) return false;
                    }
                }
            }
            return true;
        });
    }, [services, filters]);

    // Toggle Function
    const toggleFilter = (filterType: string, value: any) => {
        setFilters(prev => {
            const currentFilter = prev[filterType] || [];
            const isIncluded = currentFilter.includes(value);
            return {
                ...prev,
                [filterType]: isIncluded
                    ? currentFilter.filter(item => item !== value)
                    : [...currentFilter, value]
            };
        });
    };

    return {
        filters,
        setFilters,
        toggleFilter,
        filteredServices,
        allAttributeKeys,
        providerAttributeKeys,
        allFilterOptions,
        availableFilterOptions
    };
}
